import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AddFavouriteItemAction,
  AddFavouriteItemErrorAction,
  AddFavouriteItemSuccessAction,
  GetFavouriteItemErrorAction,
  GetFavouriteLocationsAction,
  GetFavouriteLocationsSuccessAction,
  GetFavouriteVouchersAction,
  GetFavouriteVouchersSuccessAction,
  GetFavouriteWalletsAction,
  GetFavouriteWalletsSuccessAction,
  LimitReachedFavouriteItemAction,
  RemoveFavouriteItemAction,
  RemoveFavouriteItemSuccessAction,
} from './favourite-item.actions';
import { FavouriteItemFacade } from './favourite-item.facade';
import { of } from 'rxjs';
import { getItemId } from '../utils';
import { ChooseLocationService } from '@symphony-solo/domain/choose-location/src/lib/services/choose-location.service';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src/lib';
import { ApiResponseCode } from '@symphony-solo/shared/http/src/lib';
import { VoucherService } from '@symphony-solo/domain/voucher/src/lib/services/voucher.service';
import { PurseService } from '@symphony-solo/domain/purses/src/lib/services/purse.service';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';
const FAVORITE_LIMIT = 3; // Limit for favorite items
@Injectable()
export class FavouriteItemEffects {
  constructor(
    private action$: Actions,
    private favoriteItemFacade: FavouriteItemFacade,
    private chooseLocationService: ChooseLocationService,
    private voucherService: VoucherService,
    private purseService: PurseService,
    private authenticationFacade: AuthenticationFacade
  ) {}

  protected addFavoriteItem$ = createEffect(() =>
    this.action$.pipe(
      ofType(AddFavouriteItemAction),
      withLatestFrom(this.favoriteItemFacade.favouriteItem$),
      switchMap(([action, favoriteItems]) => {
        const { itemType, item } = action; // Destructure item and type from action
        const favoriteItemsByType = favoriteItems[itemType] || [];
        const itemId = getItemId(itemType, item); // Get the ID of the item being added

        // Prevent duplicates - if the item is already a favorite, dispatch error action
        if (favoriteItemsByType.some((favItem) => getItemId(itemType, favItem) === itemId)) {
          return of(AddFavouriteItemErrorAction({ failure: 'Item already exists in favorites' }));
        }

        // Check if the limit is reached before adding the item
        if (favoriteItemsByType.length >= FAVORITE_LIMIT) {
          return of(LimitReachedFavouriteItemAction({ itemType })); // Dispatch action to update the limit reached state
        }

        // Add item to the list and dispatch success action
        const updatedFavoriteItems = [...favoriteItemsByType, item];

        return of(
          AddFavouriteItemSuccessAction({
            itemType,
            updatedFavoriteItems, // Dispatch the updated list of favorite items
          })
        );
      }),
      catchError((error) => of(AddFavouriteItemErrorAction({ failure: error.message }))) // Handle errors
    )
  );

  protected getFavouriteLocations$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetFavouriteLocationsAction),
      withLatestFrom(this.favoriteItemFacade.favouriteLocationIds$, this.authenticationFacade.accountDetails$, this.favoriteItemFacade.favouriteItem$),
      switchMap(([action, favoriteItems, accountDetails, favouriteItemList]) => {
        const newAccountID = accountDetails.accountID;
          const { accountID } = favouriteItemList;
          this.favoriteItemFacade.setAccountID(newAccountID);
          if (accountID && newAccountID !== accountID) {
            // IT WILL REMOVE THE FAVRT ITEMS
            this.favoriteItemFacade.clearFavoriteItems();
            return of(GetFavouriteLocationsSuccessAction({ locations: [] }));
          }
        return this.chooseLocationService.getChooseLocation().pipe(
          map((chooseLocation: any) => {
            const chosenLocationIds = new Set(chooseLocation.map((loc) => loc.id));

            // Convert Set to array and find unmatched favorite items
            const unmatchedFavoriteItems = [...favoriteItems].filter((favId) => !chosenLocationIds.has(favId));

            // Trigger removal actions for unmatched items
            unmatchedFavoriteItems.map((favId: number) => this.favoriteItemFacade.removeItemFromFavorites('location', favId));
            const matchingLocations = chooseLocation.filter((location) => favoriteItems.has(location.id));
            return GetFavouriteLocationsSuccessAction({ locations: matchingLocations });
          }),
          catchError((error) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting location', ApiResponseCode.Server_Error))
              .build();
            return of(GetFavouriteItemErrorAction({ failure }));
          })
        );
      }),
      catchError((error) => {
        return of(GetFavouriteItemErrorAction({ failure: error.message }));
      })
    )
  );

  protected getFavouriteVouchers$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetFavouriteVouchersAction),
      withLatestFrom(this.favoriteItemFacade.favouriteVoucherIds$),
      switchMap(([action, favoriteItems]) => {
        return this.voucherService.getVouchers().pipe(
          map((vouchers: any) => {
            const chosenVoucherIds = new Set(vouchers.map((voucher) => getItemId('voucher', voucher)));

            // Convert Set to array and find unmatched favorite items
            const unmatchedFavoriteItems = [...favoriteItems].filter((favId) => !chosenVoucherIds.has(favId));

            // Trigger removal actions for unmatched items
            unmatchedFavoriteItems.map((favId: number) => this.favoriteItemFacade.removeItemFromFavorites('voucher', favId));
            const matchingVouchers = vouchers.filter(
              (voucher) => favoriteItems.has(getItemId('voucher', voucher)) // No more error here
            );
            return GetFavouriteVouchersSuccessAction({ vouchers: matchingVouchers });
          }),
          catchError((error) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting vouchers', ApiResponseCode.Server_Error))
              .build();
            return of(GetFavouriteItemErrorAction({ failure }));
          })
        );
      }),
      catchError((error) => {
        return of(GetFavouriteItemErrorAction({ failure: error.message }));
      })
    )
  );

  protected getFavouriteWallets$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetFavouriteWalletsAction),
      withLatestFrom(this.favoriteItemFacade.favouriteWalletIds$),
      switchMap(([action, favoriteItems]) => {
        return this.purseService.getPurses().pipe(
          map((wallets: any) => {
            const matchingWallets = wallets.filter(
              (wallet) => favoriteItems.has(getItemId('wallet', wallet)) // No more error here
            );
            return GetFavouriteWalletsSuccessAction({ wallets: matchingWallets });
          }),
          catchError((error) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting wallets', ApiResponseCode.Server_Error))
              .build();
            return of(GetFavouriteItemErrorAction({ failure }));
          })
        );
      }),
      catchError((error) => {
        return of(GetFavouriteItemErrorAction({ failure: error.message }));
      })
    )
  );

  protected removeFavoriteItem$ = createEffect(() =>
    this.action$.pipe(
      ofType(RemoveFavouriteItemAction),
      withLatestFrom(this.favoriteItemFacade.favouriteItem$), // Get the current favorite items
      switchMap(([action, favoriteItems]) => {
        const { itemType, itemId } = action;

        // Remove the item from the list
        const updatedFavoriteItems = favoriteItems[itemType].filter((item) => getItemId(itemType, item) !== itemId);
        
        return of(
          RemoveFavouriteItemSuccessAction({
            itemType,
            updatedFavoriteItems,
          })
        );
      })
    )
  );
}
